import { useAppContext } from 'context/state';
import styles from './index.module.scss';

import { CookiesType, SalesBanners } from 'types/enums';
import classNames from 'classnames';
import Header from 'components/layout/Header';
import NavBar from 'components/layout/NavBar';
import { useUserContext } from 'context/user_state';
import Cookies from 'js-cookie';
import { PagesProgressBar as ProgressBar } from 'next-nprogress-bar';
import { useRouter } from 'next/router';
import { CSSProperties, ReactElement, useEffect, useRef } from 'react';
import SalesBanner from '../ui/SalesBanner';
import TechWorks from './TechWorks';

interface Props {
  children: ReactElement;
  authorized?: boolean;
  webView?: boolean;
  contentStyles?: CSSProperties;
  // only for scenario where need appContext.hideHeader is set to true by default
  hideHeaderDirectly?: boolean;
}

export default function Layout(props: Props) {
  const userContext = useUserContext();
  const appContext = useAppContext();
  const { isTabletWidth } = appContext.size;
  const router = useRouter();
  const contentRef = useRef<HTMLDivElement>(null);
  const containerPrevWidth = useRef<number>(null);
  const topBannerRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const navBar = document.querySelector('#navbar-wrapper');

    containerPrevWidth.current = contentRef.current?.getBoundingClientRect().width;

    if (!navBar) return console.warn('Page router layout', 'No navbar found');

    const cb: ResizeObserverCallback = (entries) => {
      const target = entries[0].target;
      const containerWidth = contentRef.current.getBoundingClientRect().width;

      if (!contentRef.current) return console.error('No contentRef found');

      const initialWidthNavBar = 50;
      const offset =
        target.getBoundingClientRect().width === initialWidthNavBar ||
        containerPrevWidth.current !== containerWidth
          ? '0px'
          : `${target.getBoundingClientRect().width}px`;

      containerPrevWidth.current = containerWidth;

      contentRef.current.style.marginLeft = offset;
    };

    const observer = new ResizeObserver(cb);

    observer.observe(navBar);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (process.env.TECH_WORKS) {
    const cookie = Cookies.get(CookiesType.techWorks);

    if (!cookie || (cookie && JSON.parse(cookie))) {
      return <TechWorks />;
    }
  }

  const shouldShowHeader = !appContext.hideHeader && !props.hideHeaderDirectly;
  const isStatic = !userContext.isLogged || isTabletWidth;

  return (
    <div
      className={classNames(styles.root, {
        [styles.influenceHubPage]: router?.pathname.includes('influence_hub'),
        [styles.root__withTopBanner]:
          appContext?.banners &&
          appContext?.banners?.find(
            (el) => el.position.toUpperCase() === SalesBanners.Top.toUpperCase(),
          ),
      })}
      id={'mainLayout'}>
      <SalesBanner variant={SalesBanners.Top} ref={topBannerRef} />
      {userContext.isLogged && !isTabletWidth && !props.webView && <NavBar />}
      <div
        className={classNames(styles.content, styles.contentBox, {
          [styles.content__withTopBanner]:
            appContext?.banners &&
            appContext?.banners?.find(
              (el) => el.position.toUpperCase() === SalesBanners.Top.toUpperCase(),
            ),
        })}
        ref={contentRef}
        style={props.contentStyles}>
        {shouldShowHeader && (
          <Header static={isStatic} authorized={props.authorized} topBannerRef={topBannerRef} />
        )}
        {props.children}
      </div>
      <ProgressBar options={{ showSpinner: false }} height='2px' shallowRouting color='#8e6cdf' />
    </div>
  );
}
